import { Close, DeleteTwoTone, FilterList, History } from '@mui/icons-material';
import {
  Paper,
  Box,
  Button,
  Stack,
  Skeleton,
  Tooltip,
  CircularProgress,
  Chip,
  Typography,
  MenuItem,
  Select,
  Backdrop,
  IconButton
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValidRowModel,
  GridRowClassNameParams,
  GridCellParams,
  GridPaginationModel,
  GridSortModel,
  GridActionsCellItem
} from '@mui/x-data-grid';
import { useAtom } from 'jotai';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useParams } from 'react-router-dom';

import { useExportController } from 'api/controllers/ExportController';
import { useInvoiceController } from 'api/controllers/InvoiceController';
import { useOpenBankingController } from 'api/controllers/OpenBankingController';

import {
  ApprovalGate,
  ApprovalGateApprover,
  ApprovalMode,
  ApproverStatus,
  Company,
  CompanyIban,
  DocumentType,
  EntryType,
  GateStatus,
  Invoice,
  InvoiceFilters,
  InvoiceQueryFilters,
  InvoiceRow,
  InvoiceSortColumns,
  InvoiceStages,
  OpenBankingPayment,
  OpenBankingPaymentResult,
  PaymentChannel,
  PaymentStatus
} from 'openapi';

import { ChangeLogList } from 'components/ChangeLog/ChangeLog';
import { RejectDialog } from 'components/forms/InvoiceVerificationForms/RejectDialog';
import { SelectIbanForm } from 'components/forms/SelectIbanForm';
import { OverlayDropFiles } from 'components/overlays/OverlayDropFiles';
import { OverlayLoading } from 'components/overlays/OverlayLoading';
import { PaymentMethodCard } from 'components/shared/Card/PaymentMethodCard';
import { CustomGridFooter } from 'components/shared/CustomGridFooter/CustomGridFooter';
import { GridToolbar } from 'components/shared/GridToolbar/GridToolbar';
import { ConfirmationDialog } from 'components/shared/Modal/ConfirmationDialog';
import { Modal } from 'components/shared/Modal/Modal';
import { SeverityPill } from 'components/shared/SeverityPill/SeverityPill';
import {
  showErrorToastMessage,
  showSuccessToastMessage
} from 'components/shared/Toast/Toast';

import { usePermissions } from 'context/PermissionsContext';
import { useTranslations } from 'context/TranslationContext';

import { useColumnWidths } from 'hooks/useColumnWidths';
import { useDataGridHelpers } from 'hooks/useDataGridHelpers';
import { useFileDownload } from 'hooks/useFileDownload';
import { useModal } from 'hooks/useModal';

import { DOCUMENT_CONFIGS } from 'utils/config/documentEntries';
import {
  CHECK,
  COMPANY_PARAMETER,
  ID_PARAMETER,
  ACTIONS,
  APPROVERS,
  DEFAULT_GRID_ROW_HEIGHT,
  ACCOUNTED_INVOICE_STYLE,
  PAYMENT_STATUS
} from 'utils/constants/constants';
import { INVOICES_FILTERS_EXPANDED_HEIGHT } from 'utils/constants/filters';
import {
  PAGINATION_SORT_KEYS,
  PAYMENT_CHANNELS_TRANSLATION_KEYS,
  PAYMENT_STATUS_STYLES,
  PAYMENT_STATUSES_TRANSLATION_KEYS,
  FilterKeys
} from 'utils/constants/invoices';
import { FilterType } from 'utils/enums/FilterType';
import { downloadZippedInvoices } from 'utils/helpers/downloadZippedInvoices';
import { exportTxtFile } from 'utils/helpers/exportTxtFile';
import {
  getInvoiceColumnsWithSkeleton,
  getContainerHeight,
  getCounterpartyNameRow
} from 'utils/helpers/invoiceHelpers';
import { formatMoney } from 'utils/helpers/moneyHelper';
import { isNonEmptyObject } from 'utils/helpers/objectHelper';
import {
  getUnsupportedFormatMessage,
  showWarnMessage
} from 'utils/helpers/toastHelpers';
import { ExportForPaymentForm } from 'utils/interfaces/ExportForPaymentForm';
import { InvoicesFiltersForm } from 'utils/interfaces/InvoiceProps';
import { TranslateFunction } from 'utils/interfaces/Translations';
import { AppRoutesEnum } from 'utils/routes';

import { commonDataGridContainerStyle } from 'styles/components/DataGridStyle';
import {
  invoicesDataGrid,
  separatorBoxStyles,
  gateNameTextStyles
} from 'styles/components/InvoicesDataGridStyle';

import {
  areFiltrsVisibleAtom,
  exportButtonTooltipTextAtom,
  isExportButtonDisabledAtom,
  selectedCompanyIdAtom,
  userAtom
} from 'state/state';

import { InvoicesFilterPanel } from './InvoicesFilterPanel';

const CustomNoResultsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%'
    }}
  >
    <CircularProgress />
  </Box>
);

interface InvoicesDataGridProp {
  readonly invoicesList: Invoice[];
  readonly filters: InvoiceQueryFilters;
  readonly stateRoute: string;
  readonly handleUploadInvoice?: (
    fileData: FileList | File[],
    field: string
  ) => Promise<void>;
  readonly setInvoicesList: Dispatch<SetStateAction<Invoice[]>>;
  readonly invoicesFiltersData?: InvoiceFilters;
  readonly handleApplyFilters: (data: InvoicesFiltersForm | null) => void;
  readonly handlePaginationModelChange: (
    newPaginationModel: GridPaginationModel
  ) => void;
  readonly paginationModel: GridPaginationModel;
  readonly rowCount: number;
  readonly handleSortModelChange: (sortModel: GridSortModel) => void;
  readonly fetchInvoices: () => void;
  readonly isLoading: boolean;
  readonly companies: Company[];
  readonly companyIbans?: CompanyIban[];
  readonly getColumns: (translate: TranslateFunction) => GridColDef[];
  readonly entryType: EntryType;
  readonly filterType?: FilterType;
}

export const InvoicesDataGrid = ({
  invoicesList,
  filters,
  stateRoute,
  handleUploadInvoice,
  setInvoicesList,
  invoicesFiltersData,
  handleApplyFilters,
  handlePaginationModelChange,
  rowCount,
  paginationModel,
  handleSortModelChange,
  fetchInvoices,
  isLoading,
  companies,
  companyIbans,
  getColumns,
  entryType,
  filterType
}: InvoicesDataGridProp) => {
  const navigate = useNavigate();
  const { companyId } = useParams();

  const isWithoutQuickFilter = useMemo(
    () => filterType === FilterType.Initial,
    [filterType]
  );

  const { permissions } = usePermissions();
  const { translate } = useTranslations();

  const [user] = useAtom(userAtom);
  const [selectedCompanyId] = useAtom(selectedCompanyIdAtom);

  const actionPermissions = DOCUMENT_CONFIGS[entryType].getActionPermissions({
    filterType: filterType as FilterType,
    permissions: permissions as unknown as Record<string, boolean>
  });

  const {
    canApprove,
    canReject,
    canDelete,
    canUpload,
    canBook,
    canPay,
    canCreate,
    canSettle
  } = actionPermissions;

  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal
  } = useModal();
  const {
    isOpen: isAccountModalOpen,
    openModal: openAccountModal,
    closeModal: closeAccountModal
  } = useModal();
  const {
    isOpen: isPayModalOpen,
    openModal: openPayModal,
    closeModal: closePayModal
  } = useModal();
  const {
    isOpen: isApproveModalOpen,
    openModal: openApproveModal,
    closeModal: closeApproveModal
  } = useModal();
  const {
    isOpen: isRejectModalOpen,
    openModal: openRejectModal,
    closeModal: closeRejectModal
  } = useModal();
  const {
    isOpen: isChangeLogModalOpen,
    openModal: openChangeLogModal,
    closeModal: closeChangeLogModal
  } = useModal();
  const {
    isOpen: isSettleModalOpen,
    openModal: openSettleModal,
    closeModal: closeSettleModal
  } = useModal();

  const { getSeverityColor } = useDataGridHelpers();

  const {
    deleteInvoices,
    setIsAccounted,
    markAsPaidByIds,
    approveInvoicesByIds,
    rejectInvoicesByIds,
    createWithoutDocument,
    getInvoiceFile,
    settleInvoices
  } = useInvoiceController();
  const {
    exportInvoicesPayment,
    exportInvoicesZipByIds,
    exportInvoicesExcelByFilters,
    exportInvoicesExcelByCompanyIdAndFilters
  } = useExportController();
  const { initiatePaymentByCompanyId } = useOpenBankingController();
  const { downloadFileAsBlob } = useFileDownload();

  localStorage.setItem('state', stateRoute);
  const isOnAllCompanies =
    stateRoute === AppRoutesEnum.INVOICES ||
    stateRoute === AppRoutesEnum.REVENUE;

  const isOnRevenue =
    stateRoute === AppRoutesEnum.REVENUE ||
    stateRoute === AppRoutesEnum.COMPANY_REVENUE;

  const [areFiltersVisible, setAreFiltersVisible] =
    useAtom(areFiltrsVisibleAtom);
  const [isExportButtonDisabled] = useAtom(isExportButtonDisabledAtom);
  const [exportTooltip, setExportButtonTooltipText] = useAtom(
    exportButtonTooltipTextAtom
  );

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedSingleInvoiceId, setSelectedSingleInvoiceId] =
    useState<number>();
  const [isProcessingDownload, setIsProcessingDownload] =
    useState<boolean>(false);
  const [selectedPaymentChannel, setSelectedPaymentChannel] =
    useState<PaymentChannel>(PaymentChannel.BANK);
  const [filteredEntries, setFilteredEntries] = useState<Invoice[]>([]);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);

  const handleCloseModals = () => {
    closeDeleteModal();
    closeAccountModal();
    closePayModal();
    closeApproveModal();
    closeRejectModal();
    closeRejectModal();
    closeChangeLogModal();
    closeSettleModal();
    setSelectedSingleInvoiceId(undefined);
    setSelectedRows([]);
  };

  const confirmDelete = async () => {
    handleCloseModals();
    await deleteInvoices({
      invoiceIds: selectedSingleInvoiceId
        ? [selectedSingleInvoiceId]
        : selectedRows
    });

    const updatedInvoiceList = invoicesList.filter(
      (invoice) =>
        !selectedRows.includes(invoice.id as number) ||
        invoice.id !== selectedSingleInvoiceId
    );
    setInvoicesList(updatedInvoiceList);
  };

  const confirmSettle = async () => {
    handleCloseModals();
    await settleInvoices({
      ids: selectedSingleInvoiceId ? [selectedSingleInvoiceId] : selectedRows
    });
  };

  const confirmAccount = async () => {
    handleCloseModals();
    await setIsAccounted(
      selectedSingleInvoiceId ? [selectedSingleInvoiceId] : selectedRows
    );

    const updatedInvoiceList = invoicesList.map((invoice) => {
      if (
        selectedRows.includes(invoice.id as number) ||
        invoice.id === selectedSingleInvoiceId
      ) {
        return {
          ...invoice,
          isAccounted: true
        };
      }
      return invoice;
    });
    setInvoicesList(updatedInvoiceList);
    fetchInvoices();
  };

  const confirmMarkAsPaid = async () => {
    handleCloseModals();
    await markAsPaidByIds(
      selectedPaymentChannel,
      selectedSingleInvoiceId ? [selectedSingleInvoiceId] : selectedRows
    );

    const updatedInvoiceList = invoicesList.map((invoice) => {
      if (
        selectedRows.includes(invoice.id as number) ||
        invoice.id === selectedSingleInvoiceId
      ) {
        return {
          ...invoice,
          stage: InvoiceStages.FINALIZED
        };
      }
      return invoice;
    });
    setInvoicesList(updatedInvoiceList);
    fetchInvoices();
  };

  const handleApprove = async () => {
    handleCloseModals();
    await approveInvoicesByIds(
      selectedSingleInvoiceId ? [selectedSingleInvoiceId] : selectedRows
    );
    fetchInvoices();
  };

  const handleReject = async (reason: string) => {
    handleCloseModals();
    await rejectInvoicesByIds(
      selectedSingleInvoiceId ? [selectedSingleInvoiceId] : selectedRows,
      reason
    );
    fetchInvoices();
  };

  const confirmExportForPayment = async (values: ExportForPaymentForm) => {
    handleCloseModals();
    const selectedIds = selectedSingleInvoiceId
      ? [selectedSingleInvoiceId]
      : selectedRows;
    const excludedCreditNotesCount = selectedIds.filter((id) => {
      const entry = (filteredEntries as InvoiceRow[])?.find(
        (row) => row.id === id
      );
      return entry?.documentType === DocumentType.CREDIT_NOTE;
    }).length;
    const body = {
      ibanId: values.ibanId,
      invoiceIds: selectedRows
    };
    const { fileContent, fileName } = await exportInvoicesPayment(
      Number(companyId),
      body
    );
    await exportTxtFile(fileContent, fileName);
    if (excludedCreditNotesCount > 0) {
      showSuccessToastMessage(
        translate('messages.creditNotesExcludedFromPaymentExport')
      );
    }
    fetchInvoices();
  };

  const confirmDownloadInvoices = async () => {
    handleCloseModals();
    setIsProcessingDownload(true);
    if (selectedRows.length === 1) {
      const invoiceFileData = await getInvoiceFile(selectedRows[0]);

      try {
        await downloadFileAsBlob(invoiceFileData.url, invoiceFileData.fileName);
      } finally {
        setIsProcessingDownload(false);
      }
      return;
    }

    let response;
    try {
      response = await exportInvoicesZipByIds(selectedRows);
    } finally {
      setIsProcessingDownload(false);
    }
    const { fileContent, fileName } = await response;
    await downloadZippedInvoices(fileContent, fileName);
    fetchInvoices();
  };

  const handleCreateClick = async () => {
    const createdInvoice = await createWithoutDocument(
      Number(isOnAllCompanies ? selectedCompanyId : companyId),
      { entryType }
    );

    const verificationRoute = isOnRevenue
      ? AppRoutesEnum.REVENUE_VERIFICATION
      : AppRoutesEnum.INVOICE_VERIFICATION;

    navigate(
      verificationRoute
        .replace(ID_PARAMETER, createdInvoice.id.toString())
        .replace(
          COMPANY_PARAMETER,
          String(isOnAllCompanies ? selectedCompanyId : companyId)
        ),
      { state: { invoice: stateRoute } }
    );
  };

  const handleExportToExcel = useCallback(async () => {
    if (isOnAllCompanies) {
      const response = await exportInvoicesExcelByFilters(filters);
      const { fileContent, fileName } = await response;
      await exportTxtFile(fileContent, fileName);
      return;
    }

    const response = await exportInvoicesExcelByCompanyIdAndFilters(
      Number(companyId),
      filters
    );
    const { fileContent, fileName } = await response;
    await exportTxtFile(fileContent, fileName);
  }, [filters, companyId, exportInvoicesExcelByFilters, exportTxtFile]);

  const getDisabledDeleteTooltip = useCallback(
    (row: GridValidRowModel) => {
      if (
        row.stage === InvoiceStages.UPLOADED ||
        row.stage === InvoiceStages.DRAFT
      ) {
        return translate('messages.disabledDeleteInvoicePermission');
      }
      return translate('messages.disabledDeleteInvoice');
    },
    [translate]
  );

  const onDrop = useCallback(
    (acceptedFiles: any, fileRejections: any) => {
      if (fileRejections.length > 0) {
        const { label, message } = getUnsupportedFormatMessage(fileRejections);
        const notSupportedMessage = translate(label, {
          invoices: message
        });

        showWarnMessage(notSupportedMessage);
      }

      if (handleUploadInvoice && acceptedFiles.length > 0) {
        handleUploadInvoice(acceptedFiles, 'invoiceFile');
      }
    },
    [handleUploadInvoice]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      noClick: true,
      multiple: true,
      accept: {
        'application/pdf': ['.pdf'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/heic': ['.heic']
      }
    });
  const canBeDroppable = useMemo(
    () => filterType === FilterType.Initial,
    [filterType, isOnAllCompanies]
  );

  const getApprovalCounts = useCallback((gate: ApprovalGate) => {
    const clearedApprovers =
      gate.gateApprovers?.filter(
        (approver: any) => approver.approvalStatus === ApproverStatus.APPROVED
      ).length || 0;
    const totalApprovers = gate.gateApprovers?.length || 0;
    return { clearedApprovers, totalApprovers };
  }, []);

  const getGateLabels = useCallback(
    (gate: ApprovalGate) => {
      const gateStatusLabel = translate(
        `approvals.${gate.gateStatus.toLowerCase()}`
      );
      const approvalModeLabel = gate.approvalMode
        ? translate(`approvals.${gate.approvalMode.toLowerCase()}`)
        : '-';
      return { gateStatusLabel, approvalModeLabel };
    },
    [translate]
  );

  const getApprovers = useCallback((gate: ApprovalGate) => {
    const pendingApprover = gate.gateApprovers?.find(
      (approver) => approver.approvalStatus === ApproverStatus.PENDING_APPROVAL
    );
    const rejectedApprover = gate.gateApprovers?.find(
      (approver) => approver.approvalStatus === ApproverStatus.REJECTED
    );
    return { pendingApprover, rejectedApprover };
  }, []);

  // Render approver info based on mode and status
  const renderApproverInfo = useCallback(
    (
      gate: ApprovalGate,
      isRejected: boolean,
      pendingApprover: ApprovalGateApprover | undefined,
      rejectedApprover: ApprovalGateApprover | undefined,
      totalApprovers: number,
      minimumApprovalsRequired: number
    ) => {
      if (isRejected && rejectedApprover) {
        return (
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {rejectedApprover.approver?.name}
          </Typography>
        );
      }

      if (pendingApprover) {
        if (gate.approvalMode === ApprovalMode.SEQUENTIAL) {
          return (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {pendingApprover.approver?.name}
            </Typography>
          );
        }

        if (gate.approvalMode === ApprovalMode.PARALLEL) {
          return (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {`${minimumApprovalsRequired} ${translate('approvals.eligible')}`}
            </Typography>
          );
        }
      }

      return null;
    },
    [translate]
  );

  // Render gate tooltip content
  const renderGateTooltipContent = useCallback(
    (gates: ApprovalGate[], isRejected: boolean, totalGates: number) => {
      const MAX_GATES_TO_SHOW = 6;
      const gatesToShow = gates.slice(0, MAX_GATES_TO_SHOW);
      const remainingGates = totalGates - MAX_GATES_TO_SHOW;

      return (
        <Box sx={{ maxWidth: '400px' }}>
          {gatesToShow.map((gate, index) => {
            const { clearedApprovers, totalApprovers } =
              getApprovalCounts(gate);
            const { gateStatusLabel, approvalModeLabel } = getGateLabels(gate);
            const { pendingApprover, rejectedApprover } = getApprovers(gate);
            const minimumApprovalsRequired = gate.minimumApprovalsRequired || 0;

            const isAutoApproved =
              gate.approvalMode === ApprovalMode.AUTO_APPROVE;

            return (
              <Box
                key={gate.id || index}
                sx={{
                  display: 'flex',
                  mb: 1,
                  '&:last-child': { mb: 0 }
                }}
              >
                {!isAutoApproved && (
                  <Box sx={{ mr: '0.75rem' }}>
                    <Typography variant="body2">
                      {`${index + 1}/${totalGates}`}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {isAutoApproved ? (
                    <>
                      <Typography variant="body2">
                        {translate('approvals.autoApproved')}
                      </Typography>
                      <Typography variant="body2" sx={gateNameTextStyles}>
                        {gate.name}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          {gateStatusLabel}
                        </Typography>
                        <Box sx={separatorBoxStyles} />
                        <Typography variant="body2">
                          {approvalModeLabel}
                        </Typography>
                        {gate.approvalMode === ApprovalMode.SEQUENTIAL ||
                        gate.approvalMode === ApprovalMode.PARALLEL ? (
                          <Typography variant="body2">
                            {`${clearedApprovers}/${totalApprovers}`}
                          </Typography>
                        ) : null}
                      </Box>

                      <Box display="flex" flexDirection="column" width="100%">
                        <Typography variant="body2" sx={gateNameTextStyles}>
                          {gate.name}
                        </Typography>
                        {((isRejected && rejectedApprover) ||
                          pendingApprover) && (
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {renderApproverInfo(
                              gate,
                              isRejected,
                              pendingApprover,
                              rejectedApprover,
                              totalApprovers,
                              minimumApprovalsRequired
                            )}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
          {remainingGates > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {translate(
                `messages.remaining${remainingGates === 1 ? 'Gate' : 'Gates'}`,
                {
                  remainingGates: String(remainingGates)
                }
              )}
            </Typography>
          )}
        </Box>
      );
    },
    [
      getApprovalCounts,
      getGateLabels,
      getApprovers,
      renderApproverInfo,
      translate,
      separatorBoxStyles
    ]
  );

  const columns: GridColDef[] = useMemo(() => {
    return getColumns(translate).map((column) => {
      switch (column.field) {
        case InvoiceSortColumns.STAGE:
          return {
            ...column,
            renderCell: (params: any) => {
              const { color, backgroundColor } = getSeverityColor(
                params.row[InvoiceSortColumns.STAGE]
              );
              return (
                <Box sx={{ width: '100%' }}>
                  {params.row[InvoiceSortColumns.STAGE] ===
                  InvoiceStages.UPLOADED ? (
                    <Skeleton animation="wave" />
                  ) : (
                    <SeverityPill
                      color={color}
                      backgroundColor={backgroundColor}
                    >
                      {params.row[InvoiceSortColumns.STAGE]}
                    </SeverityPill>
                  )}
                </Box>
              );
            }
          };
        case InvoiceSortColumns.INVOICE_AMOUNT:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              row[InvoiceSortColumns.STAGE] === InvoiceStages.UPLOADED ? (
                <Box sx={{ width: '100%' }}>
                  <Skeleton animation="wave" />
                </Box>
              ) : (
                `${
                  row.documentType === DocumentType.CREDIT_NOTE ? '-' : ''
                }${formatMoney(row[InvoiceSortColumns.INVOICE_AMOUNT])} ${
                  row.currency || ''
                }`
              ),
            valueGetter: (_value: any, row: any) =>
              `${formatMoney(row[InvoiceSortColumns.INVOICE_AMOUNT])} ${
                row.currency || ''
              }`
          };
        case InvoiceSortColumns.PAYMENT_STATUS:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => {
              const {
                icon: IconComponent,
                backgroundColor,
                textColor
              } = PAYMENT_STATUS_STYLES[row[PAYMENT_STATUS] as PaymentStatus];
              const label = translate(
                PAYMENT_STATUSES_TRANSLATION_KEYS[
                  row[PAYMENT_STATUS] as PaymentStatus
                ]
              );
              const paymentChannel =
                PAYMENT_CHANNELS_TRANSLATION_KEYS[
                  row.paymentChannel as PaymentChannel
                ] &&
                translate(
                  PAYMENT_CHANNELS_TRANSLATION_KEYS[
                    row.paymentChannel as PaymentChannel
                  ]
                );
              return row[InvoiceSortColumns.STAGE] ===
                InvoiceStages.UPLOADED ? (
                <Box sx={{ width: '100%' }}>
                  <Skeleton animation="wave" />
                </Box>
              ) : (
                <Tooltip title={paymentChannel}>
                  <Chip
                    label={label}
                    icon={
                      IconComponent ? (
                        <IconComponent fontSize="small" />
                      ) : undefined
                    }
                    size="small"
                    sx={{
                      backgroundColor,
                      color: textColor,
                      '& .MuiChip-icon': {
                        color: textColor
                      }
                    }}
                  />
                </Tooltip>
              );
            }
          };
        case APPROVERS:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) => {
              if (row[InvoiceSortColumns.STAGE] === InvoiceStages.UPLOADED) {
                return (
                  <Box sx={{ width: '100%' }}>
                    <Skeleton animation="wave" />
                  </Box>
                );
              }

              const gates = (row as Invoice).gates || [];
              const totalGates = gates.length;

              if (totalGates === 0) {
                return <Box sx={{ width: '100%' }}>-</Box>;
              }

              const isRejected = row.stage === InvoiceStages.REJECTED;
              const allAutoApproved =
                totalGates > 0 &&
                gates.every(
                  (gate) => gate.approvalMode === ApprovalMode.AUTO_APPROVE
                );

              const displayText = (() => {
                if (allAutoApproved) {
                  return translate('approvals.autoApproved');
                }
                if (isRejected) {
                  return translate('approvals.rejected');
                }
                const clearedCount = gates.filter(
                  (gate) => gate.gateStatus === GateStatus.CLEARED
                ).length;
                return translate('messages.gatesCleared', {
                  clearedCount: String(clearedCount),
                  total: String(totalGates)
                });
              })();

              return (
                <Tooltip
                  title={renderGateTooltipContent(
                    gates,
                    isRejected,
                    totalGates
                  )}
                  arrow
                >
                  <Box sx={{ width: '100%' }}>{displayText}</Box>
                </Tooltip>
              );
            }
          };
        case InvoiceSortColumns.COUNTERPARTY_NAME:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              row[InvoiceSortColumns.STAGE] === InvoiceStages.UPLOADED ? (
                <Box sx={{ width: '100%' }}>
                  <Skeleton animation="wave" />
                </Box>
              ) : (
                getCounterpartyNameRow(row, column, translate)
              )
          };
        case InvoiceSortColumns.INVOICE_NUMBER:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              getInvoiceColumnsWithSkeleton(
                row,
                InvoiceSortColumns.INVOICE_NUMBER
              )
          };
        case InvoiceSortColumns.INVOICE_DATE:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              getInvoiceColumnsWithSkeleton(
                row,
                InvoiceSortColumns.INVOICE_DATE
              )
          };
        case InvoiceSortColumns.INSERTED_BY:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              getInvoiceColumnsWithSkeleton(row, InvoiceSortColumns.INSERTED_BY)
          };
        case InvoiceSortColumns.EXPENSE_TYPE:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              getInvoiceColumnsWithSkeleton(
                row,
                InvoiceSortColumns.EXPENSE_TYPE
              )
          };
        case InvoiceSortColumns.INSERTED_AT:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              getInvoiceColumnsWithSkeleton(row, InvoiceSortColumns.INSERTED_AT)
          };
        case ACTIONS:
          return {
            ...column,
            width: filterType === FilterType.Initial ? 100 : 50,
            minWidth: 40,
            renderCell: ({ row }: any) => (
              <Box display="flex" justifyContent="center">
                {filterType === FilterType.Initial && (
                  <Tooltip
                    placement="left"
                    title={
                      !row.isDeletable ? getDisabledDeleteTooltip(row) : ''
                    }
                  >
                    <span>
                      <GridActionsCellItem
                        disabled={!row.isDeletable}
                        icon={
                          <DeleteTwoTone
                            sx={(theme: any) => ({
                              color: row.isDeletable
                                ? theme.palette.error.main
                                : 'default',
                              fontSize: '1.5rem'
                            })}
                            titleAccess={translate('labels.delete')}
                          />
                        }
                        label={translate('labels.delete')}
                        onClick={() => {
                          openDeleteModal();
                          setSelectedSingleInvoiceId(row.id);
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
                <GridActionsCellItem
                  icon={
                    <History
                      color="primary"
                      sx={{
                        fontSize: '1.5rem'
                      }}
                      titleAccess={translate('labels.changeLog')}
                    />
                  }
                  label={translate('labels.changeLog')}
                  onClick={() => {
                    setSelectedSingleInvoiceId(row.id);
                    openChangeLogModal();
                  }}
                />
              </Box>
            )
          };
        case InvoiceSortColumns.SHORT_COMPANY_NAME:
          return {
            ...column,
            renderCell: ({ row }: GridCellParams) =>
              getInvoiceColumnsWithSkeleton(
                row,
                InvoiceSortColumns.SHORT_COMPANY_NAME
              )
          };
        default:
          return column;
      }
    });
  }, [translate, isOnAllCompanies, filterType, user, invoicesList]);

  const { columnsWithWidths, handleColumnWidthChange } =
    useColumnWidths(columns);

  const getRowId = useCallback(
    (row: GridValidRowModel) => row.id || row.name,
    []
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<GridValidRowModel>) => {
      if (params.row.isAccounted) {
        return ACCOUNTED_INVOICE_STYLE;
      }
      return '';
    },
    []
  );

  const onCellClick = useCallback(
    (params: GridCellParams) => {
      if (
        params.field !== ACTIONS &&
        params.field !== CHECK &&
        params.row[InvoiceSortColumns.STAGE] !== InvoiceStages.UPLOADED
      ) {
        const isRevenue =
          stateRoute === AppRoutesEnum.REVENUE ||
          stateRoute === AppRoutesEnum.COMPANY_REVENUE;

        const route = isRevenue
          ? AppRoutesEnum.REVENUE_VERIFICATION
          : AppRoutesEnum.INVOICE_VERIFICATION;

        navigate(
          route
            .replace(ID_PARAMETER, params.row.id.toString())
            .replace(COMPANY_PARAMETER, params.row.companyId.toString()),
          { state: { invoice: stateRoute } }
        );
      }
    },
    [navigate, stateRoute]
  );

  const handleOpenBankingPayment = async (
    ibanId: number,
    documentIds: number[]
  ) => {
    handleCloseModals();
    setIsPaymentLoading(true);
    const requestBody: OpenBankingPayment = {
      documentIds
    };
    let result: OpenBankingPaymentResult = {};
    try {
      result = await initiatePaymentByCompanyId(
        Number(companyId),
        ibanId,
        requestBody
      );
    } finally {
      setIsPaymentLoading(false);
    }
    fetchInvoices();
    if (
      !result.url ||
      result.paymentStatus === PaymentStatus.OPEN_BANKING_FAILED
    ) {
      showErrorToastMessage(result.reasonForFail);
      return;
    }
    window.open(result.url, '_self');
  };

  useEffect(() => {
    if (invoicesList) {
      setFilteredEntries(
        invoicesList.filter((entry: any) => selectedRows?.includes(entry.id))
      );
    }
  }, [selectedRows, invoicesList]);

  const openBankingIbanOptions = useMemo(
    () => companyIbans?.filter((iban) => iban.isOpenBankingConsented),
    [companyIbans]
  );

  const exportPaymentIbanOptions = useMemo(
    () => companyIbans?.filter((iban) => iban.isExportable),
    [companyIbans]
  );

  const handleShowFilters = () => {
    setAreFiltersVisible((previousValue) => !previousValue);
  };

  const areFiltersApplied = useMemo(() => {
    if (!filters) return false;

    return Object.entries(filters).some(([key, value]) => {
      if (PAGINATION_SORT_KEYS.has(key as FilterKeys) || key === 'entryType') {
        return false;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // TODO: Refactor this
      if (value && typeof value === 'object') {
        return (
          isNonEmptyObject(value) &&
          Object.values(value).some(
            (v) => v !== null && v !== undefined && v !== ''
          )
        );
      }

      if (typeof value === 'boolean') {
        return true;
      }

      return !!value;
    });
  }, [filters]);

  const height = useMemo(
    () =>
      getContainerHeight(
        areFiltersVisible,
        INVOICES_FILTERS_EXPANDED_HEIGHT,
        isWithoutQuickFilter
      ),
    [areFiltersVisible, isWithoutQuickFilter]
  );

  const shouldShowMultiSelectInfo = useMemo(() => {
    return selectedRows.length > 1 && selectedSingleInvoiceId === undefined;
  }, [selectedRows, selectedSingleInvoiceId]);

  const shouldDisableOpenBanking = useMemo(() => {
    return (
      !openBankingIbanOptions?.length ||
      !(filteredEntries as InvoiceRow[])?.every((entry) => entry.isPayable) ||
      !filteredEntries.every(
        (entry) => entry.currency === filteredEntries[0].currency
      )
    );
  }, [openBankingIbanOptions, selectedRows, filteredEntries]);

  const openBankingTooltipText = useMemo(() => {
    if (!openBankingIbanOptions?.length) {
      return translate('messages.noAvailableIbans');
    }

    if (!(filteredEntries as InvoiceRow[])?.every((entry) => entry.isPayable)) {
      return translate('messages.disabledExportNotPayable');
    }

    if (
      !filteredEntries.every(
        (entry) => entry.currency === filteredEntries[0].currency
      )
    ) {
      return translate('messages.notAllInvoicesSameCurrency');
    }

    return '';
  }, [openBankingIbanOptions, filteredEntries]);

  useEffect(() => {
    if (!exportPaymentIbanOptions?.length) {
      setExportButtonTooltipText(translate('messages.noExportableIbans'));
    }
  }, [exportPaymentIbanOptions, setExportButtonTooltipText, translate]);

  const handleApplyAndCollapseFilters = (data: InvoicesFiltersForm | null) => {
    handleApplyFilters(data);
    setAreFiltersVisible(false);
  };

  return (
    <Stack sx={commonDataGridContainerStyle}>
      {isWithoutQuickFilter && (
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExportToExcel}
          >
            {translate('buttons.exportToExcel')}
          </Button>
          <Button
            variant={areFiltersApplied ? 'contained' : 'outlined'}
            color="primary"
            onClick={handleShowFilters}
          >
            <FilterList />
            {translate('buttons.filters')}
          </Button>
        </Box>
      )}
      {(isProcessingDownload || isPaymentLoading) && <OverlayLoading />}
      <Stack
        sx={{
          ...commonDataGridContainerStyle,
          height
        }}
        {...(canBeDroppable ? getRootProps() : {})}
      >
        {canBeDroppable && (
          <input {...getInputProps()} style={{ display: 'none' }} />
        )}
        {isWithoutQuickFilter && (
          <InvoicesFilterPanel
            handleApplyFilters={handleApplyAndCollapseFilters}
            filtersData={invoicesFiltersData}
            areFiltersVisible={areFiltersVisible}
            isOnAllCompanies={isOnAllCompanies}
            isOnRevenue={isOnRevenue}
            stateRoute={stateRoute as AppRoutesEnum}
            entryType={entryType}
          />
        )}
        <Paper
          elevation={4}
          sx={{
            ...commonDataGridContainerStyle,
            marginTop: '1rem',
            minHeight: `calc(100% - ${isOnRevenue ? '156px' : '204px'})`,
            outline: isDragActive ? '2px dashed #2196f3' : 'none',
            position: 'relative'
          }}
        >
          <DataGrid
            rows={invoicesList}
            columns={columnsWithWidths}
            getRowId={getRowId}
            loading={isLoading}
            sx={invoicesDataGrid}
            getRowClassName={getRowClassName}
            sortingMode="server"
            paginationMode="server"
            rowCount={rowCount}
            onPaginationModelChange={handlePaginationModelChange}
            paginationModel={paginationModel}
            pageSizeOptions={[]}
            slots={{
              footer: CustomGridFooter,
              toolbar: GridToolbar as any,
              noResultsOverlay: CustomNoResultsOverlay
            }}
            onSortModelChange={handleSortModelChange}
            onColumnWidthChange={handleColumnWidthChange}
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(rows) =>
              setSelectedRows(rows as number[])
            }
            rowHeight={DEFAULT_GRID_ROW_HEIGHT}
            onCellClick={onCellClick}
            checkboxSelection={permissions.INVOICES.read}
            disableRowSelectionOnClick
            disableColumnMenu
            slotProps={{
              toolbar: {
                selectedRows,
                companyIbans,
                isDefaultToolbarHidden: true,
                handleDeleteClick: canDelete ? openDeleteModal : undefined,
                handleUploadInvoice: canUpload
                  ? handleUploadInvoice
                  : undefined,
                handleAccountClick: canBook ? openAccountModal : undefined,
                handlePayClick: canPay ? openPayModal : undefined,
                handleApproveClick:
                  canApprove && filterType === FilterType.Approval
                    ? openApproveModal
                    : undefined,
                handleRejectClick:
                  canReject && filterType === FilterType.Approval
                    ? openRejectModal
                    : undefined,
                handleSettleClick: canSettle ? openSettleModal : undefined,
                handleDownloadClick: confirmDownloadInvoices,
                handleCreateClick: canCreate ? handleCreateClick : undefined,
                entries: invoicesList,
                deleteButtonTooltip: translate(
                  'messages.disabledDeleteInvoices'
                ),
                settleButtonTooltip: translate(
                  'messages.disabledSettleRevenue'
                ),
                companies,
                isOnAllCompanies
              } as any
            }}
            localeText={{
              noRowsLabel: translate('labels.noData')
            }}
          />
          {canBeDroppable && isDragActive && (
            <OverlayDropFiles isReject={isDragReject} />
          )}
          <ConfirmationDialog
            title={translate('titles.delete')}
            isOpen={isDeleteModalOpen}
            onClose={handleCloseModals}
            size="sm"
            onConfirm={confirmDelete}
          >
            {shouldShowMultiSelectInfo
              ? translate('messages.deleteInvoicesConfirmation', {
                  numberOfInvoices: selectedRows.length.toString()
                })
              : translate('messages.deleteInvoiceConfirmation')}
          </ConfirmationDialog>
          <ConfirmationDialog
            title={translate('titles.settle')}
            isOpen={isSettleModalOpen}
            onClose={handleCloseModals}
            size="sm"
            onConfirm={confirmSettle}
          >
            {shouldShowMultiSelectInfo
              ? translate('messages.settlePluralRevenueConfirmation', {
                  numberOfInvoices: selectedRows.length.toString()
                })
              : translate('messages.settleRevenueConfirmation')}
          </ConfirmationDialog>
          <ConfirmationDialog
            title={translate('titles.book')}
            isOpen={isAccountModalOpen}
            onClose={handleCloseModals}
            size="sm"
            onConfirm={confirmAccount}
          >
            {shouldShowMultiSelectInfo
              ? translate('messages.accountInvoicesConfirmation', {
                  numberOfInvoices: selectedRows.length.toString()
                })
              : translate('messages.accountInvoiceConfirmation')}
          </ConfirmationDialog>
          <Backdrop
            sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer })}
            open={isPayModalOpen}
          >
            <IconButton
              aria-label="close"
              onClick={handleCloseModals}
              sx={{
                position: 'relative',
                left: !isOnAllCompanies ? 1290 : 450,
                bottom: 120,
                color: '#fff'
              }}
            >
              <Close />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
                p: 2
              }}
            >
              <PaymentMethodCard
                tooltipText=""
                headerText={translate('buttons.markAsPaid')}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%'
                  }}
                >
                  <Box display="flex">
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        mb: 1,
                        textAlign: 'start'
                      }}
                    >
                      {translate('messages.markAsPaid')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Select
                      size="small"
                      sx={{ textAlign: 'left' }}
                      fullWidth
                      value={selectedPaymentChannel}
                      onChange={(event) =>
                        setSelectedPaymentChannel(
                          event.target.value as PaymentChannel
                        )
                      }
                    >
                      {Object.values(PaymentChannel).map((channel) => (
                        <MenuItem key={channel} value={channel}>
                          {translate(
                            PAYMENT_CHANNELS_TRANSLATION_KEYS[channel]
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button onClick={confirmMarkAsPaid} variant="contained">
                      {translate('buttons.mark')}
                    </Button>
                  </Box>
                </Box>
              </PaymentMethodCard>
              {!isOnAllCompanies && (
                <>
                  <PaymentMethodCard
                    headerText={translate('buttons.exportForPayment')}
                    disabled={
                      isExportButtonDisabled ||
                      !exportPaymentIbanOptions?.length
                    }
                    tooltipText={exportTooltip}
                    isLoading={!companyIbans}
                  >
                    <SelectIbanForm
                      onSubmit={confirmExportForPayment}
                      ibanOptions={exportPaymentIbanOptions || []}
                      disabled={
                        isExportButtonDisabled ||
                        !exportPaymentIbanOptions?.length
                      }
                      description={translate('messages.exportForPayment')}
                    />
                  </PaymentMethodCard>
                  <PaymentMethodCard
                    tooltipText={openBankingTooltipText}
                    headerText={translate('labels.openBanking')}
                    disabled={shouldDisableOpenBanking}
                    isLoading={!companyIbans}
                  >
                    <SelectIbanForm
                      onSubmit={({ ibanId }) =>
                        handleOpenBankingPayment(
                          ibanId,
                          selectedRows.length
                            ? selectedRows
                            : [selectedSingleInvoiceId!]
                        )
                      }
                      ibanOptions={openBankingIbanOptions || []}
                      isForOpenBanking
                      description={translate('messages.openBanking')}
                      disabled={shouldDisableOpenBanking}
                    />
                  </PaymentMethodCard>
                </>
              )}
            </Box>
          </Backdrop>
          <ConfirmationDialog
            title={translate('buttons.approve')}
            isOpen={isApproveModalOpen}
            onClose={handleCloseModals}
            size="sm"
            onConfirm={handleApprove}
          >
            {shouldShowMultiSelectInfo
              ? translate('messages.approveInvoicesConfirmation', {
                  numberOfInvoices: selectedRows.length.toString()
                })
              : translate('messages.approveInvoiceConfirmation')}
          </ConfirmationDialog>
          <RejectDialog
            isOpen={isRejectModalOpen}
            onClose={handleCloseModals}
            onConfirm={(formData) => {
              handleReject(formData.rejectionReason);
              handleCloseModals();
            }}
            translate={translate}
          />
          <Modal
            headerTitle={translate('titles.changeLog')}
            isOpen={isChangeLogModalOpen}
            hide={handleCloseModals}
            maxWidth="xl"
            noPaddingY
          >
            <ChangeLogList invoiceId={Number(selectedSingleInvoiceId)} />
          </Modal>
        </Paper>
      </Stack>
    </Stack>
  );
};
